/**
 * PAKETPOSITION MIT BESTANDTEILEN — die automatische Positionsbuchung (E1).
 *
 * Ist der letzte Bestandteil einer Paketposition erledigt (oder entfallen),
 * wird die POSITION im selben Vorgang gebucht: `quantityUsed` steigt auf die
 * Vertragsmenge, ins Protokoll kommt EINE Zeile mit `bookingReference =
 * 'BST-<serviceId>'` (Einmal-Schluessel, DB-Wache `bestandteil_buchung_guard`
 * aus Nr. 166). Wird danach ein Bestandteil zurueckgenommen und ist die
 * Position nicht abgerechnet, nimmt derselbe Vorgang die Buchung ueber den
 * RN-Weg zurueck (Symmetrie). Plan: docs/positionsbestandteile-plan.md,
 * Abschnitte „Nachweis" und „Automatische Positionsbuchung (E1)".
 *
 * ─── Kernregel 7: NUR an der ausloesenden Handlung ─────────────────────────
 * Gebucht wird in genau zwei Momenten: der Klick, der den letzten
 * Bestandteil erledigt (nachHandErledigung), und die Freigabe/der Versand im
 * Modul, die ihn erledigt (nachAusgabeBestandteile). NIE beim Oeffnen einer
 * Ansicht — dann stuende eine unbeteiligte Person als Buchende da (Lehre
 * Sammelnachtrag 31.08.2026). Was so nicht gebucht wird (Seite zu, Netz weg,
 * Ausgabe aus einer Sitzung ohne Schreibrecht), bucht der Nachtlauf
 * (scripts/integrity-sweep.ts, „Paketpositionen buchen").
 *
 * ─── Wer steht wo ──────────────────────────────────────────────────────────
 *  - `performerName` = BESTANDTEIL_SYSTEM_NAME: den INHALT haben mehrere
 *    Personen erbracht, sie stehen namentlich in den Bestandteilen.
 *  - `bookedBy`/`bookedById` = wer die Buchung ausgeloest hat (im Nachtlauf
 *    ebenfalls der Systemname, ohne id).
 *  - `bookedAt` = Zeitpunkt der Ausloesung; `date` = juengster Leistungstag
 *    der erledigten Bestandteile (zaehlt fuer den Leistungszeitraum).
 *  - KEIN `reportRef` (Controller-Entscheid 19.10.2026): zu viele Leser deuten
 *    ihn als Bericht. Das Kennzeichen ist die Referenz `BST-<serviceId>`.
 *
 * ─── Schreibweg: Muster B ──────────────────────────────────────────────────
 * Wie `handleTrackService` (useReportActions.ts): der neue Stand geht ueber
 * `onUpdate` (Offline-Queue, App.handleUpdateReport haelt den Sync-Anker),
 * die Zweitschrift ins Journal wird VORGEMERKT (`queueJournalMirror`, Quelle
 * 'bestandteile') und erst nach dem bestaetigten Flush angehaengt.
 *
 * ─── Ausnahme Ruecknahme: die Zeile geht ueber die Entfernungs-RPC ─────────
 * Eine Protokollzeile, die nur ueber `onUpdate` aus dem JSON faellt, haengt
 * der Riegel `service_log_guard` (Nr. 44/66/79) beim Speichern still wieder
 * an — sie traegt weder `BU-` noch `ALT-`. Entfernt wird deshalb wie im
 * Admin-Pfad (serviceReversalService) ZUERST serverseitig ueber
 * `removeServiceLogEntries` (Nr. 118: Entfernung + Zaehler + Versionspruefung
 * in EINER Transaktion, Grabstein nach Nr. 79), DANACH der Rest des Stands
 * (RN-Spur, Leistungsstand) ueber `onUpdate`. Begruendung des Journalwegs an
 * `nimmPositionsbuchungZurueck`.
 *
 * ─── Tochterprojekt ────────────────────────────────────────────────────────
 * In einer Tochter zeigt die Ansicht das LV der MUTTER; eine Buchung ginge
 * dorthin (handleTrackService, Kindzweig). Diese Automatik bucht dort NICHT
 * (Status 'abgelehnt', Grund 'tochterprojekt'): die Mutter bzw. der
 * Nachtlauf erledigt es. Erste Fassung des Plans: Verknuepfen ist ohnehin
 * gesperrt, solange eine Position Bestandteile hat.
 *
 * Die Ausfuehrungsfunktionen werfen nie — ihr Ergebnis ist ein Status.
 */

import type { BestandteilQuelle, PositionsBestandteil, Report, ServiceItem, ServiceTransaction } from '../../types';
import { getServiceLog, queueJournalMirror, voidJournalEntries } from '../serviceJournal';
import { REVERSAL_REFERENCE_PREFIX, planReversal } from '../serviceReversalLogic';
import { removeServiceLogEntries } from '../serviceLogRemoval';
import { isProjectSaveConflict } from '../projectVersionAnchor';
import { describeError } from '../errorMessage';
import { isNepService } from '../nepPosition';
import { isBuildGuardWriteError } from '../versionGate';
import { isMaintenanceWriteError } from '../maintenanceLogic';
import {
    BESTANDTEIL_SYSTEM_NAME,
    bestandteilBuchungsRef,
    bestandteilStand,
    hatBestandteile,
    heuteBerlin,
    istBestandteilBuchung,
    normalisiereBestandteile,
    standKurz,
    stundenAusDb,
    type BestandteilEintrag,
} from './bestandteilLogic';
import {
    ladeBestandteilEintraege,
    ladePaketPositionenFrisch,
    type BestandteilRpcStatus,
    type PaketPositionFrisch,
} from './bestandteilService';

// ─────────────────────────────────────────────────────────────────────────────
// Kleinkram
// ─────────────────────────────────────────────────────────────────────────────

/** Mengentoleranz (drei Stellen, wie round3 ueberall im Repo). */
const TOLERANZ = 0.0005;

const round3 = (n: number): number => Math.round(n * 1000) / 1000;

const zahl = (v: unknown): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const IST_TAG = /^\d{4}-\d{2}-\d{2}$/;

/** Gleiche Kennung? uuids kommen je nach Weg gross oder klein. */
const gleicheKennung = (a: unknown, b: unknown): boolean =>
    String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();

/**
 * Zeitwert fuer Vergleiche; Postgres liefert je nach Weg
 * '2026-10-16T12:32:00+00:00' oder '2026-10-16 12:32:00.12+00'. Unlesbar →
 * -Infinity (ganz alt). Dieselbe Normalisierung wie in bestandteilLogic.
 */
const zeitMs = (iso: string | null | undefined): number => {
    let s = String(iso ?? '').trim();
    if (!s) return -Infinity;
    if (/^\d{4}-\d{2}-\d{2} \d/.test(s)) s = s.replace(' ', 'T');
    if (/T.*[+-]\d{2}$/.test(s)) s = `${s}:00`;
    const ms = Date.parse(s);
    return Number.isFinite(ms) ? ms : -Infinity;
};

/**
 * Die aktive automatische Positionsbuchung dieser Position im Protokoll —
 * die Zeile mit `bookingReference = 'BST-<serviceId>'`. Eine
 * zurueckgenommene steht nicht mehr darin (planReversal entfernt sie), also
 * heisst „steht drin" „aktiv". Stehen (gegen die DB-Wache) zwei da, gilt die
 * juengste.
 */
export const aktiveBstBuchung = (
    serviceLog: readonly ServiceTransaction[] | null | undefined,
    serviceId: string,
): ServiceTransaction | null => {
    const ref = bestandteilBuchungsRef(serviceId);
    let beste: ServiceTransaction | null = null;
    for (const t of serviceLog || []) {
        if (!t || !istBestandteilBuchung(t) || !gleicheKennung(t.bookingReference, ref)) continue;
        if (!beste || zeitMs(t.bookedAt || t.date) > zeitMs(beste.bookedAt || beste.date)) beste = t;
    }
    return beste;
};

/**
 * Das Protokolldatum der Positionsbuchung: der juengste Leistungstag der
 * erledigten Bestandteile, sonst heute (Berlin). Ein Tag nach heute (darf es
 * nach dem RPC nicht geben) wird auf heute gekappt.
 */
export const buchungsTag = (leistungstage: readonly string[] | null | undefined, jetzt: Date): string => {
    const heute = heuteBerlin(jetzt);
    const gueltig = (leistungstage || [])
        .map((t) => String(t ?? '').trim().slice(0, 10))
        .filter((t) => IST_TAG.test(t))
        .sort();
    const juengster = gueltig[gueltig.length - 1];
    if (!juengster) return heute;
    return juengster > heute ? heute : juengster;
};

/**
 * Was die Eintraege einer Position fuer die Buchung hergeben:
 *  - `leistungstage`: die Leistungstage der AKTIVEN Eintraege mit `art =
 *    'erledigt'` (ein „entfaellt" ist keine Leistung und bestimmt kein
 *    Datum);
 *  - `letzterLabel`: das Label des zuletzt (nach `erfasstAm`) erledigten oder
 *    entfallenen Bestandteils — der Ausloeser im Nachtlauf.
 * Nur Keys der aktuellen Definition zaehlen (verwaiste Eintraege nicht).
 */
export const leistungAusEintraegen = (
    service: { id: string; bestandteile?: PositionsBestandteil[] | null },
    eintraege: readonly BestandteilEintrag[],
): { leistungstage: string[]; letzterLabel: string | null } => {
    const definition = normalisiereBestandteile(service?.bestandteile) ?? [];
    const labelJeKey = new Map(definition.map((b) => [b.key, b.label] as const));
    const leistungstage: string[] = [];
    let letzter: BestandteilEintrag | null = null;
    for (const e of eintraege || []) {
        if (!e || e.zurueckgenommenAm || !gleicheKennung(e.serviceId, service?.id)) continue;
        if (!labelJeKey.has(e.bestandteilKey)) continue;
        if (e.art === 'erledigt' && e.leistungstag) leistungstage.push(e.leistungstag);
        if (!letzter || zeitMs(e.erfasstAm) > zeitMs(letzter.erfasstAm)) letzter = e;
    }
    return { leistungstage, letzterLabel: letzter ? labelJeKey.get(letzter.bestandteilKey) ?? null : null };
};

// ─────────────────────────────────────────────────────────────────────────────
// Rein: Positionsbuchung planen
// ─────────────────────────────────────────────────────────────────────────────

export interface Ausloeser {
    userId: string | null;
    name: string;
    /**
     * Label des Bestandteils, dessen Erledigung die Buchung ausloest. Nur noch
     * Kontext fuer den Aufrufer: es steht NICHT mehr in der Protokollzeile
     * (Kernregel 6 — die `description` erscheint im Leistungsnachweis). Wer
     * ausgeloest hat, sagen `bookedBy`/`bookedById` und der Verlauf.
     */
    bestandteilLabel: string;
}

export type PositionsbuchungGrund =
    | 'kein_paket'
    | 'nicht_vollstaendig'
    | 'schon_gebucht'
    | 'menge_ungueltig'
    | 'position_fehlt'
    | 'tochterprojekt'
    | 'nur_entfallen'
    | 'nep';

/** Mindestens ein gueltiger Leistungstag — also mindestens ein aktiv ERLEDIGTER Bestandteil. */
const hatErledigtenBestandteil = (leistungstage: readonly string[] | null | undefined): boolean =>
    (leistungstage || []).some((t) => IST_TAG.test(String(t ?? '').trim().slice(0, 10)));

/**
 * REIN. Die automatische Positionsbuchung planen.
 *
 * Reihenfolge der Pruefungen (die erste gewinnt):
 *  1. `position_fehlt` — die Position steht nicht in `services`.
 *  1a. `nep` — Eventualposition (`is_optional` an der Position der Sitzung
 *     oder `frisch.istNep` aus der DB): nicht beauftragt, wird NIE automatisch
 *     gebucht — auch nicht `bewusst` (Pruefbefund 19.10.2026).
 *  2. `kein_paket` — die DB-Definition (`frisch.bestandteile`) ist leer.
 *  3. `nicht_vollstaendig` — `frisch.bestandteileOffen !== 0`. Der Wert kommt
 *     aus der DATENBANK (Trigger Nr. 166), nie aus dem Client-Stand; `null`
 *     (unbekannt) ist nicht vollstaendig (fail closed).
 *  4. `menge_ungueltig` — Vertragsmenge (DB) nicht genau 1 (E5; auch bei
 *     `bewusst`).
 *  5. `schon_gebucht` — eine aktive `BST-`-Zeile steht im Protokoll, ODER die
 *     Position ist schon voll gebucht (`quantityUsed >= quantityContracted`;
 *     es zaehlt der hoehere Wert aus DB und Sitzung — eine manuelle Buchung
 *     auf die Position schliesst nur das Gate, gebucht wird nicht doppelt).
 *  6. `nur_entfallen` — kein einziger Bestandteil ist aktiv ERLEDIGT, alle
 *     offenen sind nur „entfallen" (Controller-Entscheid 19.10.2026): dann
 *     bucht die AUTOMATIK nicht (Hand-Erledigung, Modul, Nachtlauf), denn
 *     erbracht wurde nichts. Erkannt an `leistungstage`: die Aufrufer fuellen
 *     sie ausschliesslich aus aktiven `erledigt`-Eintraegen
 *     (leistungAusEintraegen) — kein gueltiger Tag heisst kein erledigter
 *     Bestandteil. `bewusst: true` (Knopf „Position jetzt buchen" im Dialog,
 *     ein Mensch entscheidet) uebergeht NUR diese Pruefung; alle anderen
 *     Riegel bleiben.
 *
 * Menge = Vertragsmenge − gebucht; Datum = juengster Leistungstag, sonst
 * heute (Berlin). Der Leistungsstand der Position geht auf die Vertragsmenge
 * (nicht „Sitzung + Menge": wich die Sitzung von der DB ab, stuende sonst
 * nach dem Speichern weniger da als die DB schon hatte), der Status wie bei
 * `bookServiceUsage` auf 'done'.
 */
export const planePositionsbuchung = (a: {
    services: ServiceItem[];
    serviceLog: ServiceTransaction[];
    serviceId: string;
    frisch: Pick<PaketPositionFrisch, 'bestandteileOffen' | 'quantityUsed' | 'quantityContracted' | 'bestandteile' | 'istNep'>;
    leistungstage: string[];
    ausloeser: Ausloeser;
    jetzt: Date;
    neueId: () => string;
    /** Bewusste Buchung durch einen Menschen: uebergeht `nur_entfallen`, sonst nichts. */
    bewusst?: boolean;
}): { ok: true; services: ServiceItem[]; logEntry: ServiceTransaction; menge: number } | { ok: false; grund: PositionsbuchungGrund } => {
    const services = Array.isArray(a.services) ? a.services : [];
    const index = services.findIndex((s) => s && gleicheKennung(s.id, a.serviceId));
    if (index === -1) return { ok: false, grund: 'position_fehlt' };
    if (isNepService(services[index]) || a.frisch?.istNep === true) return { ok: false, grund: 'nep' };
    const definition = normalisiereBestandteile(a.frisch?.bestandteile) ?? [];
    if (definition.length === 0) return { ok: false, grund: 'kein_paket' };
    if (a.frisch.bestandteileOffen !== 0) return { ok: false, grund: 'nicht_vollstaendig' };

    // E5: Bestandteile nur bei beauftragter Menge 1. Ist die Vertragsmenge
    // spaeter eine andere geworden (Teilbeauftragung, Nachtrag), ist „alle
    // Bestandteile erledigt" nicht mehr zu deuten (einmal? zweimal?) — dann
    // bucht keiner automatisch, auch nicht bewusst (Pruefbefund F11).
    const vertrag = Number(a.frisch.quantityContracted);
    if (!Number.isFinite(vertrag) || Math.abs(vertrag - 1) > TOLERANZ) return { ok: false, grund: 'menge_ungueltig' };

    const ziel = services[index];
    if (aktiveBstBuchung(a.serviceLog, ziel.id)) return { ok: false, grund: 'schon_gebucht' };
    const gebucht = Math.max(zahl(a.frisch.quantityUsed), zahl(ziel.quantityUsed));
    if (gebucht >= vertrag - TOLERANZ) return { ok: false, grund: 'schon_gebucht' };
    const menge = round3(vertrag - gebucht);
    if (!(menge > TOLERANZ)) return { ok: false, grund: 'menge_ungueltig' };
    if (a.bewusst !== true && !hatErledigtenBestandteil(a.leistungstage)) return { ok: false, grund: 'nur_entfallen' };

    const jetzt = a.jetzt instanceof Date && Number.isFinite(a.jetzt.getTime()) ? a.jetzt : new Date();
    const userId = a.ausloeser?.userId ? String(a.ausloeser.userId) : '';
    const logEntry: ServiceTransaction = {
        id: a.neueId(),
        date: buchungsTag(a.leistungstage, jetzt),
        serviceId: ziel.id,
        quantity: menge,
        performerId: 'system',
        performerName: BESTANDTEIL_SYSTEM_NAME,
        isSubstitution: false,
        // NEUTRAL wie eine gewoehnliche Pauschalbuchung: der Positionstitel.
        // Die Zeile steht im Leistungsnachweis (generatorPerformance) — dort
        // haben Bestandteile, Labels und Ausloeser nichts zu suchen
        // (Kernregel 6). Intern erzaehlt der Verlauf, was geschah.
        description: String(ziel.title ?? '').trim() || String(ziel.lvPosition ?? '').trim() || 'Leistung',
        bookedAt: jetzt.toISOString(),
        bookedBy: String(a.ausloeser?.name ?? '').trim() || BESTANDTEIL_SYSTEM_NAME,
        ...(userId ? { bookedById: userId } : {}),
        bookingReference: bestandteilBuchungsRef(ziel.id),
    };

    const neuGebucht = round3(gebucht + menge);
    const fertig = zahl(ziel.quantityContracted) > 0 && neuGebucht >= zahl(ziel.quantityContracted) - TOLERANZ;
    const neu = [...services];
    neu[index] = { ...ziel, quantityUsed: neuGebucht, status: fertig ? 'done' : ziel.status };
    return { ok: true, services: neu, logEntry, menge };
};

// ─────────────────────────────────────────────────────────────────────────────
// Rein: Positionsbuchung zuruecknehmen planen
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Referenz der RN-Spur in der Form des Admin-Pfads (`RN-JJJJ-NNNNN`), hier
 * aus der Spur-id abgeleitet statt gewuerfelt — die Funktion bleibt rein und
 * pruefbar. Eindeutig muss sie nicht sein: die Spur traegt ihre eigene id,
 * und niemand liest die Ziffern aus.
 */
const rnReferenz = (jetzt: Date, spurId: string): string => {
    let h = 0;
    for (const c of String(spurId)) h = (h * 31 + c.charCodeAt(0)) >>> 0;
    return `${REVERSAL_REFERENCE_PREFIX}${heuteBerlin(jetzt).slice(0, 4)}-${String(h % 100000).padStart(5, '0')}`;
};

export type PositionsruecknahmeGrund = 'keine_automatische_buchung' | 'abgerechnet' | 'position_fehlt' | 'nicht_moeglich';

/**
 * REIN. Nimmt die AUTOMATISCHE Positionsbuchung (`BST-…`) zurueck — ueber
 * `planReversal`, also mit denselben Schranken wie der Admin-Pfad (nie unter
 * `quantityBilled`, stehender Verzicht sperrt, Dublette sperrt).
 *
 *  - Keine `BST-`-Zeile im Protokoll (die Position wurde von Hand gebucht):
 *    'keine_automatische_buchung'. Eine manuelle Buchung nimmt diese
 *    Automatik nie zurueck — das Gate schliesst dann nur.
 *  - `quantityBilled > 0`: 'abgerechnet' (erst Storno).
 *  - Sonstige Weigerung von planReversal: 'nicht_moeglich' mit dessen Text
 *    (Erweiterung gegenueber dem Auftrag — sonst ginge die Begruendung
 *    verloren).
 *
 * Die RN-Spur traegt AUSDRUECKLICH `reversedReportRef = 'BST-<serviceId>'`
 * (Controller-Entscheid 19.10.2026): die Positionsbuchung hat keinen
 * `reportRef`, planReversal uebertruege also nichts, und
 * `istBestandteilRuecknahme` erkennt die Spur genau an diesem Feld. Harmlos:
 * keine Buchung traegt diese Kennung als `reportRef`, und die Journalspalte
 * `reversed_report_ref` hat keinen CHECK.
 *
 * Der Leistungsstand sinkt um die Menge der Zeile; ein 'done', das damit
 * nicht mehr stimmt, wird wieder 'open' (Gegenstueck zu bookServiceUsage).
 */
export const planePositionsruecknahme = (a: {
    services: ServiceItem[];
    serviceLog: ServiceTransaction[];
    serviceId: string;
    grund: string;
    person: { userId: string | null; name: string };
    jetzt: Date;
    neueId: () => string;
}):
    | { ok: true; services: ServiceItem[]; serviceLog: ServiceTransaction[]; spur: ServiceTransaction; entfernt: ServiceTransaction }
    | { ok: false; grund: PositionsruecknahmeGrund; text?: string } => {
    const services = Array.isArray(a.services) ? a.services : [];
    const ziel = services.find((s) => s && gleicheKennung(s.id, a.serviceId));
    if (!ziel) return { ok: false, grund: 'position_fehlt' };
    const log = Array.isArray(a.serviceLog) ? a.serviceLog : [];
    const entfernt = aktiveBstBuchung(log, ziel.id);
    if (!entfernt) return { ok: false, grund: 'keine_automatische_buchung' };
    if (zahl(ziel.quantityBilled) > TOLERANZ) return { ok: false, grund: 'abgerechnet' };

    const jetzt = a.jetzt instanceof Date && Number.isFinite(a.jetzt.getTime()) ? a.jetzt : new Date();
    const spurId = a.neueId();
    const ergebnis = planReversal(ziel, log, {
        target: { kind: 'entry', entryId: String(entfernt.id) },
        reason: String(a.grund ?? ''),
        actorName: String(a.person?.name ?? '').trim() || BESTANDTEIL_SYSTEM_NAME,
        actorId: a.person?.userId || undefined,
        now: jetzt.toISOString(),
        traceId: spurId,
        reference: rnReferenz(jetzt, spurId),
        // Die Zuordnung je Eintrag braucht einen Belegzugriff; die Schranke
        // davor (`quantityBilled > 0` → abgerechnet) faengt jede ausgestellte
        // Rechnung, die diese Position je beruehrt hat. Der Server prueft
        // dasselbe noch einmal (bestandteil_zuruecknehmen: quantity_billed).
        allocatedQuantity: 0,
    });
    if (!ergebnis.ok || !ergebnis.plan) {
        const text = ergebnis.error || 'Die Rücknahme ist nicht möglich.';
        return /abgerechnet/i.test(text) ? { ok: false, grund: 'abgerechnet', text } : { ok: false, grund: 'nicht_moeglich', text };
    }

    const plan = ergebnis.plan;
    const roheSpur = plan.log.find((t) => t && t.id === spurId);
    if (!roheSpur) return { ok: false, grund: 'nicht_moeglich', text: 'Die Rücknahmespur fehlt im Plan.' };
    const spur: ServiceTransaction = { ...roheSpur, reversedReportRef: bestandteilBuchungsRef(ziel.id) };
    const serviceLog = plan.log.map((t) => (t === roheSpur ? spur : t));

    const neuGebucht = zahl(plan.service.quantityUsed);
    const offenWieder = plan.service.status === 'done' && neuGebucht < zahl(plan.service.quantityContracted) - TOLERANZ;
    const dienst: ServiceItem = offenWieder ? { ...plan.service, status: 'open' } : plan.service;
    return {
        ok: true,
        services: services.map((s) => (s === ziel ? dienst : s)),
        serviceLog,
        spur,
        entfernt,
    };
};

// ─────────────────────────────────────────────────────────────────────────────
// Ausfuehrung (Muster B)
// ─────────────────────────────────────────────────────────────────────────────

export interface BuchungsKontext {
    report: Report;
    onUpdate: (r: Report) => void;
    user: { id: string | null; name: string };
    /**
     * OPTIONAL (Erweiterung gegenueber dem Auftrag): liefert den Stand, der
     * JETZT in der Sitzung gilt. Die async-Funktionen unten haben eine
     * Netzrunde zwischen Aufruf und `onUpdate`; hat das Modul in der Zeit
     * selbst `onUpdate` gerufen (Freigabe gespeichert), waere `report`
     * ueberholt und `onUpdate` setzte dessen Aenderung zurueck. Wer es
     * uebergibt, schliesst das Fenster; ohne gilt `report`.
     */
    holeBericht?: () => Report;
}

export type AusfuehrungsStatus = 'gebucht' | 'zurueckgenommen' | 'nicht_noetig' | 'abgelehnt' | 'fehler';

/** Gruende, die heissen „es gibt nichts zu tun" — kein Fehler, keine Weigerung. */
const NICHTS_ZU_TUN: ReadonlySet<string> = new Set(['kein_paket', 'nicht_vollstaendig', 'schon_gebucht']);

const aktuellerBericht = (ctx: BuchungsKontext): Report => {
    try {
        return ctx.holeBericht?.() ?? ctx.report;
    } catch {
        return ctx.report;
    }
};

const projektIdVon = (r: Report | null | undefined): string | null => {
    const id = r?.metadata?.globalData?.projectId;
    return id && id !== 'temp-id' ? id : null;
};

const istTochter = (r: Report | null | undefined): boolean =>
    r?.metadata?.globalData?.linkedProjectConfig?.role === 'child';

/**
 * Bucht eine vollstaendige Paketposition ueber `onUpdate` (+ Vormerkung der
 * Zweitschrift, Quelle 'bestandteile') — Muster B wie handleTrackService.
 *
 * Tochterprojekt: NICHT buchen ('abgelehnt', 'tochterprojekt') — die Buchung
 * ginge an die Mutter; das erledigt die Mutter bzw. der Nachtlauf.
 *
 * Schreibt im Stand auch `bestandteileOffen` der Position auf den frischen
 * DB-Wert (nur Anzeige; gesendet wird das Feld nie, cloudService nimmt es
 * heraus).
 *
 * Sind alle Bestandteile nur „entfallen" (keiner erledigt), bucht die
 * Automatik nicht ('abgelehnt', 'nur_entfallen'). `bewusst: true` ist der Weg
 * fuer den Knopf „Position jetzt buchen" (ein Mensch entscheidet): er uebergeht
 * nur diese Pruefung; ohne Leistungstage gilt heute (Berlin).
 *
 * Synchron: zwischen Planen und `onUpdate` liegt keine Netzrunde.
 */
export const bucheVollstaendigePosition = (
    ctx: BuchungsKontext & {
        serviceId: string;
        frisch: PaketPositionFrisch;
        leistungstage: string[];
        bestandteilLabel: string;
        bewusst?: boolean;
    },
): { status: AusfuehrungsStatus; grund?: string; logEntry?: ServiceTransaction } => {
    try {
        const bericht = aktuellerBericht(ctx);
        if (istTochter(bericht)) return { status: 'abgelehnt', grund: 'tochterprojekt' };
        const globalData = bericht?.metadata?.globalData;
        if (!globalData) return { status: 'abgelehnt', grund: 'position_fehlt' };
        const vorher = getServiceLog(bericht);
        const plan = planePositionsbuchung({
            services: globalData.services || [],
            serviceLog: vorher,
            serviceId: ctx.serviceId,
            frisch: ctx.frisch,
            leistungstage: ctx.leistungstage,
            ausloeser: { userId: ctx.user?.id ?? null, name: ctx.user?.name ?? '', bestandteilLabel: ctx.bestandteilLabel },
            jetzt: new Date(),
            neueId: () => crypto.randomUUID(),
            bewusst: ctx.bewusst === true,
        });
        if (plan.ok === false) {
            return { status: NICHTS_ZU_TUN.has(plan.grund) ? 'nicht_noetig' : 'abgelehnt', grund: plan.grund };
        }
        const services = plan.services.map((s) =>
            gleicheKennung(s.id, ctx.serviceId) && hatBestandteile(s) ? { ...s, bestandteileOffen: ctx.frisch.bestandteileOffen } : s,
        );
        ctx.onUpdate({
            ...bericht,
            metadata: {
                ...bericht.metadata,
                globalData: { ...globalData, services, serviceLog: [...(globalData.serviceLog || []), plan.logEntry] },
            },
        });
        // Muster B: vormerken, angehaengt wird nach dem bestaetigten Flush.
        // Keine `statements`: die Zeile ist weder Zusatzleistung noch
        // Begehungsnachweis, die Abbildung erschliesst 'work' aus der Referenz.
        const projectId = projektIdVon(bericht);
        if (projectId) {
            queueJournalMirror({
                projectId,
                entries: [plan.logEntry],
                previousLog: vorher,
                source: 'bestandteile',
                linkedProjectConfig: globalData.linkedProjectConfig ?? null,
            });
        }
        return { status: 'gebucht', logEntry: plan.logEntry };
    } catch (e) {
        return { status: 'fehler', grund: describeError(e) };
    }
};

/**
 * Nimmt die automatische Positionsbuchung (`BST-…`) zurueck.
 *
 * ─── Ablauf ────────────────────────────────────────────────────────────────
 *  1. Planen (planePositionsruecknahme) auf dem Stand der Sitzung.
 *  2. `removeServiceLogEntries` mit der Zeile UND dem neuen Leistungsstand
 *     (Nr. 118: eine Transaktion, Versionspruefung gegen den Anker, Grabstein
 *     nach Nr. 79). Nur so bleibt die Zeile draussen — ueber `onUpdate`
 *     allein haengte der Riegel `service_log_guard` sie beim Speichern wieder
 *     an (Arbeitszeile ohne `BU-`/`ALT-`). Reihenfolge wie im Admin-Pfad:
 *     RPC → Speichern → Journal.
 *     - Konflikt (jemand hat seit dem Laden gespeichert): nichts geaendert,
 *       'fehler'/'konflikt' — neu laden, erneut versuchen. Build-Riegel bzw.
 *       Bauphase: 'fehler'/'build_veraltet' bzw. 'bauphase', nichts geaendert.
 *     - `legacy` (Nr. 66 fehlt): die Zeile liesse sich nicht entfernen,
 *       'fehler'/'entfernung_nicht_verfuegbar', NICHTS geschrieben. Die
 *       Position bleibt gebucht, ihr Gate ist durch den offenen Bestandteil
 *       ohnehin zu (nicht abrechenbar), der Projektcheck meldet
 *       `position_gebucht_bestandteile_offen`.
 *     - Zeile auf dem Server nicht gefunden: 'fehler'/'nicht_auf_dem_server'
 *       (andere Sitzung war schneller, oder die Buchung ist noch nicht
 *       hochgeladen) — neu laden.
 *  3. `onUpdate` mit dem Rest: Zeile raus, RN-Spur rein, Leistungsstand.
 *  4. Journal:
 *     - die RN-Spur wird VORGEMERKT (`queueJournalMirror`) — sie existiert nur
 *       im Stand der Sitzung und kommt erst mit deren Flush ins JSON (Muster
 *       B; sofort anhaengen hiesse, eine Journalzeile ohne JSON-Zeile zu
 *       riskieren, und das Journal kennt kein Delete);
 *     - die ENTFERNTE Zeile wird SOFORT stillgelegt (`voidJournalEntries`,
 *       Grund 'correction'), und zwar fuer genau die ids, die der Server als
 *       entfernt gemeldet hat — NICHT erst nach dem Flush. Begruendung: aus dem
 *       JSON ist sie zu diesem Zeitpunkt schon weg (die RPC hat sie
 *       serverseitig genommen, der Grabstein haelt sie draussen), unabhaengig
 *       davon, ob der Flush der Sitzung je kommt. Bliebe die Journalzeile bis
 *       zum Flush lebend und die Seite ginge zu, stuende sie als `nur_journal`
 *       in der Abweichungssicht — und dort „hat das Journal recht": eine
 *       Abrechnung aus dem Journal forderte die Position. Dasselbe Kriterium
 *       nimmt der Admin-Pfad in seinem Fehlerzweig (`entfernung.removedIds`).
 *       Grund 'correction' und nicht 'storno': es wird eine Buchung
 *       berichtigt, keine Rechnungsbuchung zurueckgenommen.
 *
 * `bestandteileOffen` (optional, Erweiterung): der frische DB-Wert der
 * Position fuer die Anzeige im Stand — `nachBestandteilRuecknahme` reicht ihn
 * durch.
 */
export const nimmPositionsbuchungZurueck = async (
    ctx: BuchungsKontext & { serviceId: string; grund: string; bestandteileOffen?: number | null },
): Promise<{ status: AusfuehrungsStatus; grund?: string }> => {
    try {
        const bericht0 = aktuellerBericht(ctx);
        if (istTochter(bericht0)) return { status: 'abgelehnt', grund: 'tochterprojekt' };
        const projectId = projektIdVon(bericht0);
        if (!projectId || !bericht0.metadata.globalData) return { status: 'fehler', grund: 'projekt_ohne_id' };
        const plan = planePositionsruecknahme({
            services: bericht0.metadata.globalData.services || [],
            serviceLog: getServiceLog(bericht0),
            serviceId: ctx.serviceId,
            grund: ctx.grund,
            person: { userId: ctx.user?.id ?? null, name: ctx.user?.name ?? '' },
            jetzt: new Date(),
            neueId: () => crypto.randomUUID(),
        });
        if (plan.ok === false) {
            return {
                status: plan.grund === 'keine_automatische_buchung' ? 'nicht_noetig' : 'abgelehnt',
                grund: plan.text ? `${plan.grund}: ${plan.text}` : plan.grund,
            };
        }
        const dienst = plan.services.find((s) => gleicheKennung(s.id, ctx.serviceId));
        const zeilenId = String(plan.entfernt.id);

        let entfernung: Awaited<ReturnType<typeof removeServiceLogEntries>>;
        try {
            entfernung = await removeServiceLogEntries(
                projectId,
                [zeilenId],
                `Automatische Positionsbuchung ${bestandteilBuchungsRef(ctx.serviceId)} zurückgenommen `
                    + `(${plan.spur.bookingReference}), Bestandteil wieder offen: ${String(ctx.grund ?? '').trim()}`,
                { counters: [{ serviceId: String(dienst?.id ?? ctx.serviceId), quantityUsed: zahl(dienst?.quantityUsed) }] },
            );
        } catch (e) {
            if (isProjectSaveConflict(e)) return { status: 'fehler', grund: 'konflikt' };
            if (isBuildGuardWriteError(e)) return { status: 'fehler', grund: 'build_veraltet' };
            if (isMaintenanceWriteError(e)) return { status: 'fehler', grund: 'bauphase' };
            return { status: 'fehler', grund: describeError(e) };
        }
        if (entfernung.mode === 'legacy') return { status: 'fehler', grund: 'entfernung_nicht_verfuegbar' };
        if (!entfernung.removedIds.some((id) => gleicheKennung(id, zeilenId))) {
            return { status: 'fehler', grund: 'nicht_auf_dem_server' };
        }

        // Den Rest auf den Stand legen, der JETZT gilt (holeBericht): zwischen
        // Planen und hier lag die RPC. Uebernommen werden nur Leistungsstand
        // und Status der einen Position, die Zeile faellt heraus, die Spur
        // kommt dazu — alles andere bleibt, wie die Sitzung es inzwischen hat.
        const aktuell = aktuellerBericht(ctx);
        const bericht1 = aktuell?.metadata?.globalData ? aktuell : bericht0;
        const gd1 = bericht1.metadata.globalData!;
        const vorher1 = getServiceLog(bericht1);
        const services1 = (gd1.services || []).map((s) =>
            gleicheKennung(s.id, ctx.serviceId) && dienst
                ? {
                    ...s,
                    quantityUsed: dienst.quantityUsed,
                    status: dienst.status,
                    ...(ctx.bestandteileOffen !== undefined && hatBestandteile(s) ? { bestandteileOffen: ctx.bestandteileOffen } : {}),
                }
                : s,
        );
        const log1 = [plan.spur, ...vorher1.filter((t) => !gleicheKennung(t?.id, zeilenId))];
        ctx.onUpdate({
            ...bericht1,
            metadata: { ...bericht1.metadata, globalData: { ...gd1, services: services1, serviceLog: log1 } },
        });
        queueJournalMirror({
            projectId,
            entries: [plan.spur],
            previousLog: vorher1,
            source: 'bestandteile',
            linkedProjectConfig: gd1.linkedProjectConfig ?? null,
        });
        // Wirft nie (serviceJournal); siehe Begruendung oben.
        await voidJournalEntries(projectId, entfernung.removedIds, 'correction');
        return { status: 'zurueckgenommen' };
    } catch (e) {
        return { status: 'fehler', grund: describeError(e) };
    }
};

// ─────────────────────────────────────────────────────────────────────────────
// Die drei Einstiege fuer Dialog und Module
// ─────────────────────────────────────────────────────────────────────────────

export interface AusgabeFolge {
    /** Bestandteile, die DIESE Ausgabe erledigt hat — mit Stand der Position („5/5"). */
    erledigt: Array<{
        serviceId: string;
        positionTitel: string;
        bestandteilLabel: string;
        stand: string;
        /**
         * Der aktive Eintrag zu Position + Key, an dem die Erledigung haengt
         * (bevorzugt der mit `ausgabeId` = diese Ausgabe) — das Pflichtfenster
         * fuer die Stunden (Stundenkontingent S3) traegt sie dort nach. `null`:
         * nicht ermittelbar; dann gibt es kein Fenster, nur den Hinweis.
         */
        eintragId: string | null;
        /** Stunden dieses Eintrags — `null`: noch keine (die Modul-Erledigung per Trigger setzt nie welche). */
        stunden: number | null;
        /**
         * true: die Datenbank kennt die Stunden noch nicht (Spalte `stunden`
         * fehlt, Nr. 171/172 nicht gefahren — `stundenVerfuegbar` false). Dann
         * sind sie UNBEKANNT, nicht „fehlen": kein Pflichtfenster, kein gelber
         * Satz (der Dialog blendet den Stundenbereich in diesem Zustand aus).
         * Fehlt sonst.
         */
        stundenUnbekannt?: true;
    }>;
    /** Positionen, die dadurch vollstaendig wurden und jetzt gebucht sind. */
    gebucht: Array<{ serviceId: string; positionTitel: string }>;
    /** Alles, was der Anwender wissen sollte (offen gebliebene Bestandteile, Lesefehler, Weigerungen). */
    hinweise: string[];
}

/**
 * Zeitfenster, in dem ein Modul-Eintrag OHNE bekannte `ausgabeId` als „von
 * dieser Ausgabe erledigt" gilt. Der Trigger (Nr. 166) setzt `erfasst_am` auf
 * den Zeitpunkt der Ausgabe, und der Aufruf folgt ihr unmittelbar. Ohne
 * Fenster meldete der Schritt einen Eintrag von letzter Woche als Ergebnis
 * dieser Freigabe — genau dann, wenn die Automatik diesmal NICHT gegriffen hat
 * (zwei Pakete mit derselben Quelle, Kernregel 9).
 */
const AUSGABE_FENSTER_MS = 15 * 60 * 1000;

/**
 * Nach Freigabe/Versand in einem Modul.
 *
 * 1. Frischen Stand lesen (Positionen + Eintraege).
 * 2. Melden, welcher Bestandteil dieser Quelle durch GENAU diese Ausgabe
 *    erledigt wurde (`ausgabeId` bekannt), sonst der juengste Modul-Eintrag
 *    dieser Quelle im Zeitfenster.
 * 3. Offen gebliebene Bestandteile dieser Quelle als Hinweis (Uebernahme im
 *    Dialog).
 * 4. Positionen aus 2., die dadurch vollstaendig sind (DB sagt 0 offen), SOFORT
 *    buchen (bucheVollstaendigePosition). NUR diese — eine schon frueher
 *    vollstaendige, ungebuchte Position gehoert nicht zu dieser Handlung
 *    (Kernregel 7), sie bucht der Nachtlauf.
 *    Und NUR mit `ausgabeId` (Pruefbefund F8): ohne sie ist der Treffer aus 2.
 *    nur „der juengste Modul-Eintrag" — womoeglich die Ausgabe einer anderen
 *    Person, und `bookedBy` stuende falsch. Dann wird nur gemeldet; gebucht
 *    wird im Dialog oder im Nachtlauf.
 *
 * Wirft nie; Fehler stehen in `hinweise`.
 */
export const nachAusgabeBestandteile = async (
    ctx: BuchungsKontext & { projectId: string; quelle: BestandteilQuelle; ausgabeId?: string | null },
): Promise<AusgabeFolge> => {
    const folge: AusgabeFolge = { erledigt: [], gebucht: [], hinweise: [] };
    try {
        const [pos, ein] = await Promise.all([ladePaketPositionenFrisch(ctx.projectId), ladeBestandteilEintraege(ctx.projectId)]);
        if (pos.ok === false) {
            folge.hinweise.push(pos.fehler);
            return folge;
        }
        const betroffen = pos.positionen.filter((p) => (p.bestandteile || []).some((b) => b.quelle === ctx.quelle));
        if (betroffen.length === 0) return folge;
        if (ein.ok === false) {
            folge.hinweise.push(ein.fehler);
            return folge;
        }

        const labelVon = (p: PaketPositionFrisch, key: string): PositionsBestandteil | undefined =>
            (p.bestandteile || []).find((b) => b.key === key && b.quelle === ctx.quelle);
        const aktive = ein.eintraege.filter((e) => !e.zurueckgenommenAm);

        // ── 2. Was hat diese Ausgabe erledigt? ───────────────────────────────
        // `e` ist der aktive Eintrag zu Position + Key, an dem die Erledigung
        // haengt: mit `ausgabeId` genau der dieser Ausgabe, sonst der juengste
        // Modul-Eintrag im Zeitfenster (aktiv je Key hoechstens einer — der
        // Teil-Unique aus Nr. 165). An ihm traegt das Pflichtfenster die
        // Stunden nach (S3).
        type Treffer = { p: PaketPositionFrisch; b: PositionsBestandteil; e: BestandteilEintrag };
        const treffer: Treffer[] = [];
        const ausgabeId = String(ctx.ausgabeId ?? '').trim();
        if (ausgabeId) {
            for (const e of aktive) {
                if (!gleicheKennung(e.ausgabeId, ausgabeId)) continue;
                const p = betroffen.find((x) => gleicheKennung(x.id, e.serviceId));
                const b = p ? labelVon(p, e.bestandteilKey) : undefined;
                if (p && b) treffer.push({ p, b, e });
            }
        } else {
            const grenze = Date.now() - AUSGABE_FENSTER_MS;
            let bester: { t: Treffer; ms: number } | null = null;
            for (const e of aktive) {
                if (e.durch !== 'modul') continue;
                const ms = zeitMs(e.erfasstAm);
                if (ms < grenze) continue;
                const p = betroffen.find((x) => gleicheKennung(x.id, e.serviceId));
                const b = p ? labelVon(p, e.bestandteilKey) : undefined;
                if (p && b && (!bester || ms > bester.ms)) bester = { t: { p, b, e }, ms };
            }
            if (bester) treffer.push(bester.t);
        }
        // Kennt die Datenbank die Stunden nicht (Nr. 171 fehlt), sind sie
        // unbekannt — nicht „fehlen": kein Pflichtfenster, das nur an
        // `rpc_fehlt` scheitern koennte.
        const stundenUnbekannt = ein.stundenVerfuegbar === false;
        for (const { p, b, e } of treffer) {
            const eintragId = String(e.id ?? '').trim();
            folge.erledigt.push({
                serviceId: p.id,
                positionTitel: p.title,
                bestandteilLabel: b.label,
                stand: standKurz(bestandteilStand({ id: p.id, bestandteile: p.bestandteile }, ein.eintraege)),
                eintragId: eintragId || null,
                stunden: stundenUnbekannt ? null : stundenAusDb(e.stunden),
                ...(stundenUnbekannt ? { stundenUnbekannt: true as const } : {}),
            });
        }

        // ── 3. Offen gebliebene Bestandteile dieser Quelle ───────────────────
        for (const p of betroffen) {
            const stand = bestandteilStand({ id: p.id, bestandteile: p.bestandteile }, ein.eintraege);
            for (const z of stand.zeilen) {
                if (z.status !== 'offen' || z.bestandteil.quelle !== ctx.quelle) continue;
                folge.hinweise.push(
                    `Bestandteil „${z.bestandteil.label}“ der Position „${p.title}“ ist noch offen — `
                    + 'die Ausgabe hat ihn nicht automatisch erledigt (mehrdeutig oder erst beim Versand). '
                    + 'In der Liste der Bestandteile übernehmen.',
                );
            }
        }

        // ── 4. Vollstaendig gewordene Positionen buchen ──────────────────────
        // Mehrere Buchungen nacheinander muessen aufeinander aufbauen: ohne
        // `holeBericht` des Aufrufers fuehrt diese Kette den Stand selbst.
        let letzter: Report | null = null;
        const kette: BuchungsKontext = {
            ...ctx,
            holeBericht: () => letzter ?? aktuellerBericht(ctx),
            onUpdate: (r) => {
                letzter = r;
                ctx.onUpdate(r);
            },
        };
        const gesehen = new Set<string>();
        for (const { p, b } of treffer) {
            if (gesehen.has(p.id) || p.bestandteileOffen !== 0) continue;
            gesehen.add(p.id);
            if (!ausgabeId) {
                folge.hinweise.push(
                    `Position „${p.title}“ ist vollständig — ohne Bezug auf diese Ausgabe wird nicht automatisch gebucht `
                    + '(Dialog oder Nachtlauf).',
                );
                continue;
            }
            const leistung = leistungAusEintraegen({ id: p.id, bestandteile: p.bestandteile }, ein.eintraege);
            const r = bucheVollstaendigePosition({
                ...kette,
                serviceId: p.id,
                frisch: p,
                leistungstage: leistung.leistungstage,
                bestandteilLabel: b.label,
            });
            if (r.status === 'gebucht') {
                folge.gebucht.push({ serviceId: p.id, positionTitel: p.title });
            } else if (r.grund === 'tochterprojekt') {
                folge.hinweise.push(`Position „${p.title}“ ist vollständig — gebucht wird im Mutterprojekt bzw. im Nachtlauf.`);
            } else if (r.grund === 'schon_gebucht') {
                folge.hinweise.push(`Position „${p.title}“ war bereits gebucht.`);
            } else if (r.grund === 'nep') {
                folge.hinweise.push(`Position „${p.title}“ ist eine Eventualposition (NEP) — sie wird nie automatisch gebucht.`);
            } else if (r.grund === 'nur_entfallen') {
                folge.hinweise.push(`Position „${p.title}“: alle Bestandteile entfallen — nicht automatisch gebucht, bitte im Dialog bewusst buchen.`);
            } else if (r.status !== 'nicht_noetig') {
                folge.hinweise.push(`Position „${p.title}“ nicht gebucht (${r.grund ?? r.status}) — der Nachtlauf holt es nach.`);
            }
        }
    } catch (e) {
        folge.hinweise.push(`Bestandteile: ${describeError(e)}`);
    }
    return folge;
};

/**
 * Nach einer Hand-Erledigung im Dialog: ist die Position laut DB vollstaendig,
 * wird sie gebucht (frischer Stand).
 *
 * `offenDanach` (optional, Erweiterung): der Wert aus der RPC-Antwort. Ist er
 * bekannt und nicht 0, gibt es nichts zu tun — ohne Netzrunde. Wichtig bei
 * zwei gleichzeitigen Sitzungen: nur die, deren Erledigung die letzte war,
 * bekam 0 und bucht.
 *
 * Die Leistungstage kommen aus den Eintraegen; sind die nicht lesbar, wird
 * NICHT gebucht ('fehler') — das Datum zaehlt fuer den Leistungszeitraum,
 * raten waere schlechter als die Buchung des Nachtlaufs.
 */
export const nachHandErledigung = async (
    ctx: BuchungsKontext & { projectId: string; serviceId: string; bestandteilLabel: string; offenDanach?: number | null },
): Promise<{ status: AusfuehrungsStatus; grund?: string }> => {
    try {
        if (ctx.offenDanach !== undefined && ctx.offenDanach !== 0) return { status: 'nicht_noetig', grund: 'nicht_vollstaendig' };
        const [pos, ein] = await Promise.all([ladePaketPositionenFrisch(ctx.projectId), ladeBestandteilEintraege(ctx.projectId)]);
        if (pos.ok === false) return { status: 'fehler', grund: pos.fehler };
        const frisch = pos.positionen.find((p) => gleicheKennung(p.id, ctx.serviceId));
        if (!frisch) return { status: 'nicht_noetig', grund: 'kein_paket' };
        if (frisch.bestandteileOffen !== 0) return { status: 'nicht_noetig', grund: 'nicht_vollstaendig' };
        if (ein.ok === false) return { status: 'fehler', grund: ein.fehler };
        const leistung = leistungAusEintraegen({ id: frisch.id, bestandteile: frisch.bestandteile }, ein.eintraege);
        const r = bucheVollstaendigePosition({
            ...ctx,
            serviceId: frisch.id,
            frisch,
            leistungstage: leistung.leistungstage,
            bestandteilLabel: String(ctx.bestandteilLabel ?? '').trim() || leistung.letzterLabel || '',
        });
        return { status: r.status, ...(r.grund ? { grund: r.grund } : {}) };
    } catch (e) {
        return { status: 'fehler', grund: describeError(e) };
    }
};

/**
 * Nach der Ruecknahme eines Bestandteils: gibt es eine aktive `BST-`-Buchung
 * und ist die Position laut DB nicht mehr vollstaendig und nicht abgerechnet,
 * wird die Buchung zurueckgenommen (nimmPositionsbuchungZurueck).
 *
 *  - keine `BST-`-Zeile → 'nicht_noetig' (manuell gebucht oder nie gebucht)
 *  - Position kein Paket mehr (Definition entfernt) → 'nicht_noetig'
 *  - DB sagt weiter 0 offen → 'nicht_noetig' ('noch_vollstaendig')
 *  - abgerechnet → 'abgelehnt' (erst Storno)
 *  - Abrechnungsstand unbekannt (`quantity_billed` NULL) → 'abgelehnt'
 *    ('abrechnungsstand_unbekannt') — nie als 0 lesen.
 *
 * `rpcStatus` (optional, Erweiterung): der Status der vorangegangenen
 * `bestandteil_zuruecknehmen`-RPC. Ist er bekannt und nicht
 * 'zurueckgenommen' (z. B. `position_fehlt`, `abrechnungsstand_unbekannt`,
 * `veraltet`, `abgerechnet`), wird an der Positionsbuchung NICHTS getan —
 * ohne Netzrunde. Der Dialog sollte ihn immer mitgeben.
 */
export const nachBestandteilRuecknahme = async (
    ctx: BuchungsKontext & { projectId: string; serviceId: string; grund: string; rpcStatus?: BestandteilRpcStatus },
): Promise<{ status: AusfuehrungsStatus; grund?: string }> => {
    try {
        if (ctx.rpcStatus !== undefined && ctx.rpcStatus !== 'zurueckgenommen') {
            return { status: 'nicht_noetig', grund: `rpc_${ctx.rpcStatus}` };
        }
        const bericht = aktuellerBericht(ctx);
        if (!aktiveBstBuchung(getServiceLog(bericht), ctx.serviceId)) {
            return { status: 'nicht_noetig', grund: 'keine_automatische_buchung' };
        }
        const pos = await ladePaketPositionenFrisch(ctx.projectId);
        if (pos.ok === false) return { status: 'fehler', grund: pos.fehler };
        const frisch = pos.positionen.find((p) => gleicheKennung(p.id, ctx.serviceId));
        if (!frisch) return { status: 'nicht_noetig', grund: 'kein_paket' };
        if (frisch.bestandteileOffen === 0) return { status: 'nicht_noetig', grund: 'noch_vollstaendig' };
        if (frisch.quantityBilledBekannt === false) return { status: 'abgelehnt', grund: 'abrechnungsstand_unbekannt' };
        if (frisch.quantityBilled > TOLERANZ) return { status: 'abgelehnt', grund: 'abgerechnet' };
        const grund = String(ctx.grund ?? '').trim();
        return await nimmPositionsbuchungZurueck({
            ...ctx,
            serviceId: frisch.id,
            grund: `Bestandteil zurückgenommen${grund ? `: ${grund}` : ''}`,
            bestandteileOffen: frisch.bestandteileOffen,
        });
    } catch (e) {
        return { status: 'fehler', grund: describeError(e) };
    }
};
